import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../services/api';
import './InstallerApp.css';

// Origen del backend para servir archivos subidos (en dev el front va en :3000)
const API_ORIGIN  = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000';
const API_BASE    = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:4000/api';

// ── Helpers de fecha ──────────────────────────────────────────────────────────
const parseDT = s => (s ? new Date(String(s).replace(' ', 'T')) : null);
const pad = n => String(n).padStart(2, '0');
function toInputDT(s) {
  const d = parseDT(s); if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromInputDT = v => (v ? v.replace('T', ' ') + ':00' : null);
function fmtDT(s) {
  const d = parseDT(s); if (!d) return 'Sin fecha';
  return d.toLocaleString('es-ES', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const STATUS = {
  pendiente:   { label: 'Pendiente',   cls: 'ia-st--pend' },
  confirmada:  { label: 'Confirmada',  cls: 'ia-st--conf' },
  reagendada:  { label: 'Reagendada',  cls: 'ia-st--reag' },
  en_curso:    { label: 'En curso',    cls: 'ia-st--curso' },
  completada:  { label: 'Completada',  cls: 'ia-st--done' },
};
const ACCESS = [{ v: 'facil', l: 'Fácil' }, { v: 'dificil', l: 'Difícil' }, { v: 'grua', l: 'Requiere grúa' }];

// Geolocalización best-effort: si el usuario deniega o falla, devuelve {} (no bloquea el trabajo)
function getGeo() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}
const mapsUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
function fmtTime(s) { const d = parseDT(s); return d ? d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''; }
const monthKey = s => { const d = parseDT(s); return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}` : ''; };
const monthLabel = k => { if (!k) return ''; const [y, m] = k.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); };
const notifIcon = t => ({ visit_new: '📋', installation_new: '🔧', reschedule: '🗓', reminder: '⏰' })[t] || '🔔';

// ── Subida multipart ──────────────────────────────────────────────────────────
async function uploadFile(file, { visit_id, installation_id, type, day_number }, token) {
  const fd = new FormData();
  fd.append('file', file);
  if (visit_id) fd.append('visit_id', visit_id);
  if (installation_id) fd.append('installation_id', installation_id);
  fd.append('type', type || 'foto');
  if (day_number != null) fd.append('day_number', day_number);
  const res = await fetch(API_BASE + '/installer-files', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || 'Error subiendo el archivo');
  return d;
}

// ── PWA: manifest + service worker (solo bajo /installer) ─────────────────────
function registerInstallerPWA() {
  if (!document.querySelector('link[rel="manifest"][data-installer]')) {
    const l = document.createElement('link');
    l.rel = 'manifest'; l.href = '/installer-manifest.json'; l.setAttribute('data-installer', '1');
    document.head.appendChild(l);
  }
  let tc = document.querySelector('meta[name="theme-color"]');
  if (!tc) { tc = document.createElement('meta'); tc.name = 'theme-color'; document.head.appendChild(tc); }
  tc.content = '#E84300';
  // El SW ya no intercepta fetch, así que registrarlo no interfiere con el HMR del CRM
  if ('serviceWorker' in navigator) navigator.serviceWorker.register('/installer-sw.js').catch(() => {});
}

// ── Web Push (notificaciones al móvil) ─────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
async function enablePush(token) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) throw new Error('Este navegador no soporta notificaciones push');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Permiso de notificaciones denegado');
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { publicKey } = await apiGet('/installer-notifications/vapid-public', token);
    if (!publicKey) throw new Error('Push no configurado en el servidor');
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  }
  await apiPost('/installer-notifications/subscribe', sub.toJSON(), token);
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function InstallerApp() {
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('installer_auth')) || null; } catch { return null; }
  });
  useEffect(() => {
    if (auth) sessionStorage.setItem('installer_auth', JSON.stringify(auth));
    else sessionStorage.removeItem('installer_auth');
  }, [auth]);
  useEffect(() => { registerInstallerPWA(); }, []);

  if (!auth) return <InstallerLogin onLogin={setAuth} />;
  if (auth.user?.role !== 'installer') {
    return (
      <div className="ia-shell ia-center">
        <div className="ia-card ia-msg">
          <h2>Acceso solo para instaladores</h2>
          <p>Esta cuenta no es de instalador.</p>
          <button className="ia-btn ia-btn--ghost" onClick={() => setAuth(null)}>Cerrar sesión</button>
        </div>
      </div>
    );
  }
  return <InstallerHome auth={auth} onLogout={() => setAuth(null)} />;
}

// ── Login ─────────────────────────────────────────────────────────────────────
function InstallerLogin({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try { onLogin(await apiPost('/auth/login', form)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  return (
    <div className="ia-shell ia-center">
      <form className="ia-card ia-login" onSubmit={submit}>
        <div className="ia-brand"><span className="ia-brand-g">g</span>estavin <small>Instaladores</small></div>
        <input className="ia-input" type="email" placeholder="Email" autoComplete="username"
          value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
        <input className="ia-input" type="password" placeholder="Contraseña" autoComplete="current-password"
          value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
        {err && <p className="ia-err">{err}</p>}
        <button className="ia-btn ia-btn--primary" disabled={busy}>{busy ? 'Entrando…' : 'Entrar'}</button>
      </form>
    </div>
  );
}

// ── Home: lista de trabajos ───────────────────────────────────────────────────
function InstallerHome({ auth, onLogout }) {
  const token = auth.token;
  const [jobs, setJobs] = useState(null);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('activos');
  const [fMonth, setFMonth] = useState('');
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pushPerm, setPushPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [pushMsg, setPushMsg] = useState('');
  async function activatePush() {
    setPushMsg('Activando…');
    try { await enablePush(token); setPushPerm('granted'); setPushMsg(''); }
    catch (e) { setPushMsg(e.message); }
  }

  const loadNotifs = useCallback(async () => {
    try { setNotifs(await apiGet('/installer-notifications', token)); } catch {}
  }, [token]);
  useEffect(() => { loadNotifs(); const iv = setInterval(loadNotifs, 60000); return () => clearInterval(iv); }, [loadNotifs]);
  const unread = notifs.filter(n => !n.is_read).length;
  async function openNotifs() {
    setNotifOpen(true);
    if (unread) { try { await apiPost('/installer-notifications/read-all', {}, token); } catch {} loadNotifs(); }
  }

  const load = useCallback(async () => {
    setErr('');
    try {
      const [visits, installs] = await Promise.all([
        apiGet('/visits', token),
        apiGet('/installations', token),
      ]);
      const v = (visits || []).map(x => ({ ...x, kind: 'visit' }));
      const n = (installs || []).map(x => ({ ...x, kind: 'installation' }));
      const all = [...v, ...n].sort((a, b) => (parseDT(a.scheduled_at) || 0) - (parseDT(b.scheduled_at) || 0));
      setJobs(all);
    } catch (e) { setErr(e.message); setJobs([]); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const now = new Date();
  const activos = (jobs || []).filter(j => j.status !== 'completada');
  const realizados = (jobs || []).filter(j => j.status === 'completada');
  const groups = { hoy: [], proximos: [] };
  activos.forEach(j => { if (sameDay(parseDT(j.scheduled_at), now)) groups.hoy.push(j); else groups.proximos.push(j); });
  const months = [...new Set(realizados.map(j => monthKey(j.scheduled_at)).filter(Boolean))].sort().reverse();
  const realizadosShown = fMonth ? realizados.filter(j => monthKey(j.scheduled_at) === fMonth) : realizados;

  const selected = sel ? (jobs || []).find(j => j.kind === sel.kind && j.id === sel.id) : null;

  return (
    <div className="ia-shell">
      <header className="ia-header">
        <div>
          <div className="ia-hello">Hola, {auth.user?.name?.split(' ')[0] || 'instalador'}</div>
          <div className="ia-sub">{activos.length} activos · {realizados.length} realizados</div>
        </div>
        <div className="ia-header-actions">
          <button className="ia-iconbtn ia-bell" onClick={openNotifs} title="Notificaciones">
            🔔{unread > 0 && <span className="ia-badge">{unread}</span>}
          </button>
          <button className="ia-iconbtn" onClick={load} title="Actualizar">↻</button>
          <button className="ia-iconbtn" onClick={onLogout} title="Salir">⎋</button>
        </div>
      </header>

      <div className="ia-tabs">
        <button className={`ia-tab ${tab === 'activos' ? 'ia-tab--on' : ''}`} onClick={() => setTab('activos')}>Trabajos ({activos.length})</button>
        <button className={`ia-tab ${tab === 'calendario' ? 'ia-tab--on' : ''}`} onClick={() => setTab('calendario')}>Calendario</button>
        <button className={`ia-tab ${tab === 'realizados' ? 'ia-tab--on' : ''}`} onClick={() => setTab('realizados')}>Realizados ({realizados.length})</button>
      </div>

      <main className="ia-main">
        {jobs === null ? <div className="ia-loading">Cargando…</div> : (
          <>
            {err && <p className="ia-err">{err}</p>}
            {pushPerm !== 'granted' && pushPerm !== 'unsupported' && (
              <div className="ia-pushbar">
                <span>🔔 Activa los avisos en este móvil para recibir visitas, instalaciones y recordatorios.</span>
                <button className="ia-btn ia-btn--primary" onClick={activatePush}>Activar avisos</button>
                {pushMsg && <span className="ia-pushmsg">{pushMsg}</span>}
              </div>
            )}
            {tab === 'activos' ? (
              <>
                <Summary jobs={jobs} />
                {activos.length === 0 ? <div className="ia-empty">No tienes trabajos activos.</div> : (
                  <>
                    <Section title="Hoy"      jobs={groups.hoy}      onOpen={setSel} />
                    <Section title="Próximos" jobs={groups.proximos} onOpen={setSel} />
                  </>
                )}
              </>
            ) : tab === 'calendario' ? (
              <CalendarView jobs={jobs} onOpen={setSel} />
            ) : (
              <>
                {months.length > 0 && (
                  <select className="ia-input ia-month" value={fMonth} onChange={e => setFMonth(e.target.value)}>
                    <option value="">Todos los meses</option>
                    {months.map(m => <option key={m} value={m} style={{ textTransform: 'capitalize' }}>{monthLabel(m)}</option>)}
                  </select>
                )}
                {realizadosShown.length === 0 ? <div className="ia-empty">Sin trabajos realizados{fMonth ? ' este mes' : ''}.</div>
                  : realizadosShown.map(j => <JobCard key={j.kind + j.id} job={j} onOpen={setSel} muted />)}
              </>
            )}
          </>
        )}
      </main>

      {selected && (
        <JobDetail job={selected} token={token} onClose={() => setSel(null)} onChanged={load} />
      )}

      {notifOpen && (
        <div className="ia-overlay" onClick={() => setNotifOpen(false)}>
          <div className="ia-sheet" onClick={e => e.stopPropagation()}>
            <div className="ia-sheet-head"><span style={{ fontWeight: 800 }}>Notificaciones</span>
              <button className="ia-iconbtn" onClick={() => setNotifOpen(false)}>✕</button></div>
            <div className="ia-sheet-body">
              {notifs.length === 0 ? <div className="ia-empty">Sin notificaciones.</div>
                : notifs.map(n => (
                  <div key={n.id} className={`ia-notif ${n.is_read ? '' : 'ia-notif--new'}`}>
                    <div className="ia-notif-t">{notifIcon(n.type)} {n.title}</div>
                    {n.body && <div className="ia-notif-b">{n.body}</div>}
                    <div className="ia-notif-d">{fmtDT(n.created_at)}</div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, jobs, onOpen, muted }) {
  if (!jobs.length) return null;
  return (
    <section className="ia-section">
      <h3 className="ia-section-ttl">{title}</h3>
      {jobs.map(j => <JobCard key={j.kind + j.id} job={j} onOpen={onOpen} muted={muted} />)}
    </section>
  );
}

function JobCard({ job, onOpen, muted }) {
  const st = STATUS[job.status] || { label: job.status, cls: '' };
  return (
    <button className={`ia-card ia-job ${muted ? 'ia-job--muted' : ''}`} onClick={() => onOpen(job)}>
      <div className={`ia-job-kind ia-job-kind--${job.kind}`}>{job.kind === 'visit' ? 'Visita' : 'Instalación'}</div>
      <div className="ia-job-main">
        <div className="ia-job-name">{job.customer_name || 'Cliente'}</div>
        {job.address && <div className="ia-job-addr">📍 {job.address}</div>}
        <div className="ia-job-when">🗓 {fmtDT(job.scheduled_at)}</div>
      </div>
      <span className={`ia-st ${st.cls}`}>{st.label}</span>
    </button>
  );
}

// ── Resumen ───────────────────────────────────────────────────────────────────
function Summary({ jobs }) {
  const now = new Date();
  const inMonth = j => { const d = parseDT(j.scheduled_at); return d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth(); };
  const visits = jobs.filter(j => j.kind === 'visit' && inMonth(j));
  const installs = jobs.filter(j => j.kind === 'installation' && inMonth(j));
  const pend = arr => arr.filter(j => j.status !== 'completada').length;
  const done = arr => arr.filter(j => j.status === 'completada').length;
  const cards = [
    { n: pend(visits),   l: 'Visitas pendientes' },
    { n: pend(installs), l: 'Instalaciones pendientes' },
    { n: done(visits),   l: 'Visitas realizadas' },
    { n: done(installs), l: 'Instalaciones realizadas' },
  ];
  return (
    <>
      <div className="ia-sum-title">Este mes:</div>
      <div className="ia-summary">
        {cards.map((c, i) => <div key={i} className="ia-sum-card"><div className="ia-sum-n">{c.n}</div><div className="ia-sum-l">{c.l}</div></div>)}
      </div>
    </>
  );
}

// ── Calendario (sincronizado con las citas) ────────────────────────────────────
const WD = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
function CalendarView({ jobs, onOpen }) {
  const today = new Date();
  const [cur, setCur] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selDay, setSelDay] = useState(today.getDate());

  const first = new Date(cur.y, cur.m, 1);
  const offset = (first.getDay() + 6) % 7; // lunes primero
  const daysInMonth = new Date(cur.y, cur.m + 1, 0).getDate();
  const byDay = {};
  jobs.forEach(j => { const d = parseDT(j.scheduled_at); if (d && d.getFullYear() === cur.y && d.getMonth() === cur.m) { (byDay[d.getDate()] = byDay[d.getDate()] || []).push(j); } });

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLbl = first.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  const go = step => { setSelDay(null); setCur(c => { let m = c.m + step, y = c.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } return { y, m }; }); };
  const selJobs = selDay ? (byDay[selDay] || []).slice().sort((a, b) => (parseDT(a.scheduled_at) || 0) - (parseDT(b.scheduled_at) || 0)) : [];

  return (
    <div>
      <div className="ia-cal-head">
        <button className="ia-iconbtn" onClick={() => go(-1)}>‹</button>
        <div className="ia-cal-month">{monthLbl}</div>
        <button className="ia-iconbtn" onClick={() => go(1)}>›</button>
      </div>
      <div className="ia-cal-grid ia-cal-wd">{WD.map(w => <div key={w} className="ia-cal-wdc">{w}</div>)}</div>
      <div className="ia-cal-grid">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="ia-cal-cell ia-cal-cell--empty" />;
          const list = byDay[d] || [];
          const isToday = today.getFullYear() === cur.y && today.getMonth() === cur.m && today.getDate() === d;
          const hasV = list.some(j => j.kind === 'visit');
          const hasI = list.some(j => j.kind === 'installation');
          return (
            <button key={i} className={`ia-cal-cell${selDay === d ? ' ia-cal-cell--sel' : ''}${isToday ? ' ia-cal-cell--today' : ''}`} onClick={() => setSelDay(d)}>
              <span className="ia-cal-d">{d}</span>
              {list.length > 0 && <span className="ia-cal-dots">{hasV && <i className="ia-dot ia-dot--v" />}{hasI && <i className="ia-dot ia-dot--i" />}</span>}
            </button>
          );
        })}
      </div>
      {selDay && (
        <div className="ia-cal-day">
          <div className="ia-section-ttl">Día {selDay}</div>
          {selJobs.length === 0 ? <div className="ia-empty">Sin citas este día.</div> : selJobs.map(j => <JobCard key={j.kind + j.id} job={j} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}

// ── Detalle de trabajo ────────────────────────────────────────────────────────
function JobDetail({ job, token, onClose, onChanged }) {
  const isVisit = job.kind === 'visit';
  const base = isVisit ? '/visits' : '/installations';
  const [files, setFiles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reagOpen, setReagOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [newDT, setNewDT] = useState(toInputDT(job.scheduled_at));
  const [reagReason, setReagReason] = useState('');
  const [access, setAccess] = useState(job.access_type || '');
  const [delivery, setDelivery] = useState(job.delivery_notes || '');
  const [croquis, setCroquis] = useState(job.croquis_notes || '');
  const [plannedDays, setPlannedDays] = useState(job.planned_install_days ?? '');
  const [daySummary, setDaySummary] = useState('');
  const [msg, setMsg] = useState('');

  const loadFiles = useCallback(async () => {
    const q = isVisit ? `visit_id=${job.id}` : `installation_id=${job.id}`;
    try { setFiles(await apiGet(`/installer-files?${q}`, token)); } catch {}
  }, [isVisit, job.id, token]);
  const loadSessions = useCallback(async () => {
    try { setSessions(await apiGet(`${base}/${job.id}/sessions`, token)); } catch {}
  }, [base, job.id, token]);
  useEffect(() => { loadFiles(); loadSessions(); }, [loadFiles, loadSessions]);

  const st        = STATUS[job.status] || { label: job.status, cls: '' };
  const total     = Number(job.duration_days) || 1;
  const daysDone  = Number(job.days_done) || 0;
  const activeDay = job.active_day != null ? Number(job.active_day) : null;
  const completed = job.status === 'completada';
  const pendingResched = !!job.reschedule_at;
  const canConfirm    = job.status === 'pendiente' || job.status === 'reagendada';
  const canReschedule = !completed && activeDay == null && daysDone === 0 && !pendingResched;
  const inProgress    = activeDay != null;
  const canStart      = !completed && !inProgress && daysDone < total && (job.status === 'confirmada' || job.status === 'en_curso');
  // Recuentos por tipo/fase
  const croquisCount = files.filter(f => f.type === 'croquis').length;
  const zonePhotos   = isVisit ? files.filter(f => f.type !== 'croquis').length : 0;
  const ctxPhotos    = isVisit ? zonePhotos : files.filter(f => Number(f.day_number) === activeDay).length;
  const canUpload = !completed && inProgress;

  async function patch(body, okMsg) {
    setBusy(true); setMsg('');
    try { await apiPut(`${base}/${job.id}`, body, token); setMsg(okMsg || 'Guardado'); onChanged(); }
    catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  // Solicitar reagendado (lo aprueba el admin desde el CRM)
  async function requestReschedule() {
    setBusy(true); setMsg('');
    try {
      await apiPost(`${base}/${job.id}/reschedule-request`, { proposed_at: fromInputDT(newDT), reason: reagReason }, token);
      setMsg('Solicitud de reagendado enviada · pendiente de aprobación');
      setReagOpen(false); setReagReason(''); onChanged();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  // Iniciar / finalizar día con geolocalización
  async function doDay(action, extra = {}) {
    setBusy(true); setMsg('📍 Obteniendo ubicación…');
    const geo = await getGeo();
    try {
      const r = await apiPost(`${base}/${job.id}/${action}`, { ...geo, ...extra }, token);
      const loc = geo.lat ? ' · ubicación registrada' : ' · sin ubicación (permiso denegado)';
      if (action === 'start-day') setMsg(`Trabajo iniciado — día ${r.day}${loc}`);
      else setMsg(r.completed ? '✅ Trabajo completado' : `Día ${r.day} finalizado${loc}`);
      setFinishOpen(false); setDaySummary('');
      onChanged(); loadSessions();
      if (action === 'finish-day' && r.completed) setTimeout(onClose, 1100);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  async function onPick(e, type = 'foto') {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true); setMsg('');
    try {
      await uploadFile(file, isVisit
        ? { visit_id: job.id, type }
        : { installation_id: job.id, type: 'foto', day_number: activeDay }, token);
      setMsg(type === 'croquis' ? 'Croquis subido' : 'Foto subida'); loadFiles();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  // Agrupar fotos por fase
  const photoGroups = isVisit
    ? [
        { key: 'croquis', label: 'Croquis', items: files.filter(f => f.type === 'croquis') },
        { key: 'zona',    label: 'Fotos de la zona', items: files.filter(f => f.type !== 'croquis') },
      ]
    : Object.entries(files.reduce((acc, f) => { const d = Number(f.day_number) || 0; (acc[d] = acc[d] || []).push(f); return acc; }, {}))
        .sort((a, b) => a[0] - b[0])
        .map(([d, items]) => ({
          key: d,
          label: Number(d) === 0 ? 'Otras fotos' : (Number(d) === total ? `Final de obra · día ${d}` : `Día ${d}`),
          items,
        }));

  return (
    <div className="ia-overlay" onClick={onClose}>
      <div className="ia-sheet" onClick={e => e.stopPropagation()}>
        <div className="ia-sheet-head">
          <span className={`ia-job-kind ia-job-kind--${job.kind}`}>{isVisit ? 'Visita técnica' : 'Instalación'}</span>
          <button className="ia-iconbtn" onClick={onClose}>✕</button>
        </div>

        <div className="ia-sheet-body">
          <h2 className="ia-detail-name">{job.customer_name || 'Cliente'}</h2>
          <div className="ia-detail-rows">
            {job.address && <div className="ia-row"><span>Dirección</span><b>{job.address}</b></div>}
            {job.customer_phone && <div className="ia-row"><span>Teléfono</span><b><a href={`tel:${job.customer_phone}`}>{job.customer_phone}</a></b></div>}
            <div className="ia-row"><span>Fecha</span><b>{fmtDT(job.scheduled_at)}</b></div>
            <div className="ia-row"><span>Estado</span><b className={`ia-st ${st.cls}`}>{st.label}</b></div>
            {!isVisit && <div className="ia-row"><span>Duración</span><b>{total} día{total > 1 ? 's' : ''}</b></div>}
            {job.notes && <div className="ia-row"><span>Notas</span><b>{job.notes}</b></div>}
          </div>

          {/* Solicitud de reagendado pendiente de aprobación */}
          {pendingResched && (
            <div className="ia-pending">
              ⏳ Reagendado solicitado para <b>{fmtDT(job.reschedule_at)}</b> — pendiente de aprobación del administrador.
              {job.reschedule_reason && <div className="ia-pending-r">Motivo: {job.reschedule_reason}</div>}
            </div>
          )}

          {/* Progreso de días (instalaciones multi-día) */}
          {total > 1 && (
            <div className="ia-days">
              {Array.from({ length: total }, (_, i) => i + 1).map(d => {
                const state = d <= daysDone ? 'done' : (d === activeDay ? 'active' : 'todo');
                return <span key={d} className={`ia-day ia-day--${state}`}>Día {d}{state === 'done' ? ' ✓' : state === 'active' ? ' ●' : ''}</span>;
              })}
            </div>
          )}

          {/* Datos de la VISITA técnica: solo lectura si ya está completada */}
          {isVisit && completed && (job.access_type || job.delivery_notes || job.croquis_notes) && (
            <div className="ia-block">
              <div className="ia-block-ttl">Datos de la visita</div>
              <div className="ia-detail-rows">
                {job.access_type && <div className="ia-row"><span>Acceso</span><b>{(ACCESS.find(a => a.v === job.access_type) || {}).l || job.access_type}</b></div>}
                {job.delivery_notes && <div className="ia-row"><span>Entrega</span><b>{job.delivery_notes}</b></div>}
                {job.croquis_notes && <div className="ia-row"><span>Croquis</span><b>{job.croquis_notes}</b></div>}
              </div>
            </div>
          )}

          {/* Datos de la VISITA técnica: acceso, entrega y croquis (editable) */}
          {isVisit && !completed && (
            <div className="ia-block">
              <div className="ia-block-ttl">Datos de la visita</div>
              <label className="ia-lbl">Acceso para el transporte</label>
              <div className="ia-access-btns">
                {ACCESS.map(a => (
                  <button key={a.v} className={`ia-chip ${access === a.v ? 'ia-chip--on' : ''}`} onClick={() => setAccess(a.v)}>{a.l}</button>
                ))}
              </div>
              <label className="ia-lbl">Entrega / indicaciones (dejar en calle, dentro, subir a la vivienda…)</label>
              <textarea className="ia-input" rows={2} placeholder="Ej: el cliente quiere que subamos el material a la vivienda…"
                value={delivery} onChange={e => setDelivery(e.target.value)} />
              <label className="ia-lbl">Croquis — nota (nº de postes, lamas, detalles)</label>
              <textarea className="ia-input" rows={2} placeholder="Ej: 8 postes, 40 lamas, refuerzo en esquina derecha…"
                value={croquis} onChange={e => setCroquis(e.target.value)} />
              <div className="ia-hint">La imagen del croquis se sube abajo, en Fotos.</div>
              <label className="ia-lbl">Días de instalación — corrobora los previstos por la oficina</label>
              <input className="ia-input" type="number" min="1" max="6" placeholder="Días necesarios para instalar"
                value={plannedDays} onChange={e => setPlannedDays(e.target.value)} />
              <button className="ia-btn ia-btn--ghost" disabled={busy}
                onClick={() => patch({ access_type: access || null, delivery_notes: delivery, croquis_notes: croquis, planned_install_days: plannedDays === '' ? null : Number(plannedDays) }, 'Datos de la visita guardados')}>
                Guardar datos de la visita
              </button>
            </div>
          )}

          {/* Instalación: acceso heredado de la visita técnica (solo lectura) */}
          {!isVisit && (job.visit_access_type || job.visit_access_notes) && (
            <div className="ia-block">
              <div className="ia-block-ttl">Acceso a la vivienda (indicado en la visita)</div>
              <div className="ia-detail-rows">
                {job.visit_access_type && <div className="ia-row"><span>Acceso</span><b>{(ACCESS.find(a => a.v === job.visit_access_type) || {}).l || job.visit_access_type}</b></div>}
                {job.visit_access_notes && <div className="ia-row"><span>Notas</span><b>{job.visit_access_notes}</b></div>}
              </div>
            </div>
          )}

          {/* Partes de trabajo (con geolocalización y resumen) */}
          {sessions.length > 0 && (
            <div className="ia-block">
              <div className="ia-block-ttl">Partes de trabajo</div>
              {sessions.map(s => (
                <div key={s.id} className="ia-ses">
                  <div className="ia-ses-day">Día {s.day_number}</div>
                  <div className="ia-ses-times">
                    <span>▶ {fmtTime(s.started_at)} {s.start_lat != null && <a href={mapsUrl(s.start_lat, s.start_lng)} target="_blank" rel="noreferrer">📍</a>}</span>
                    <span>{s.finished_at
                      ? <>⏹ {fmtTime(s.finished_at)} {s.finish_lat != null && <a href={mapsUrl(s.finish_lat, s.finish_lng)} target="_blank" rel="noreferrer">📍</a>}</>
                      : <em>en curso…</em>}</span>
                    {s.summary && <span className="ia-ses-sum">“{s.summary}”</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Fotos organizadas por fase */}
          <div className="ia-block">
            <div className="ia-block-ttl">Fotos {isVisit ? '(croquis + mín. 3 de la zona)' : '(mínimo 3 por día)'}</div>
            {photoGroups.map(g => (
              <div key={g.key} className="ia-photo-group">
                <div className="ia-photo-glbl">{g.label} ({g.items.length})</div>
                {g.items.length > 0 && (
                  <div className="ia-thumbs">
                    {g.items.map(f => (
                      <a key={f.id} href={API_ORIGIN + f.url} target="_blank" rel="noreferrer" className="ia-thumb">
                        <img src={API_ORIGIN + f.url} alt={f.filename} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {canUpload ? (isVisit ? (
              <>
                <div className="ia-photo-count">Croquis: {croquisCount} {croquisCount >= 1 ? '✓' : '(sube 1)'} · Zona: {zonePhotos}/3 {zonePhotos >= 3 ? '✓' : ''}</div>
                <div className="ia-two">
                  <label className="ia-btn ia-btn--ghost ia-upload">📐 Croquis
                    <input type="file" accept="image/*" capture="environment" onChange={e => onPick(e, 'croquis')} hidden /></label>
                  <label className="ia-btn ia-btn--ghost ia-upload">📷 Foto zona
                    <input type="file" accept="image/*" capture="environment" onChange={e => onPick(e, 'foto')} hidden /></label>
                </div>
              </>
            ) : (
              <>
                <div className="ia-photo-count">Fotos día {activeDay}: {ctxPhotos}/3 {ctxPhotos >= 3 && '✓'}</div>
                <label className="ia-btn ia-btn--ghost ia-upload">
                  📷 Subir foto · día {activeDay}
                  <input type="file" accept="image/*" capture="environment" onChange={e => onPick(e, 'foto')} hidden />
                </label>
              </>
            )) : !completed && (
              <div className="ia-hint">Inicia el trabajo para subir las fotos.</div>
            )}
          </div>

          {msg && <p className="ia-msg-line">{msg}</p>}
        </div>

        {/* Acciones — Confirmar → Iniciar → Finalizar (por día, con requisitos) */}
        <div className="ia-sheet-actions">
          {completed ? (
            <div className="ia-done-banner">✅ Trabajo completado</div>
          ) : reagOpen ? (
            <div className="ia-reag">
              <label className="ia-lbl">Nueva fecha propuesta (la aprueba el administrador)</label>
              <input className="ia-input" type="datetime-local" value={newDT} onChange={e => setNewDT(e.target.value)} />
              <textarea className="ia-input" rows={2} placeholder="Motivo del reagendado (opcional)" value={reagReason} onChange={e => setReagReason(e.target.value)} />
              <div className="ia-reag-btns">
                <button className="ia-btn ia-btn--ghost" onClick={() => setReagOpen(false)} disabled={busy}>Cancelar</button>
                <button className="ia-btn ia-btn--primary" disabled={busy || !newDT} onClick={requestReschedule}>
                  Enviar solicitud
                </button>
              </div>
            </div>
          ) : finishOpen ? (
            <div className="ia-reag">
              <label className="ia-lbl">Resumen del trabajo del día {activeDay} (obligatorio)</label>
              <textarea className="ia-input" rows={3} placeholder="Qué se ha hecho hoy…" value={daySummary} onChange={e => setDaySummary(e.target.value)} />
              <div className="ia-photo-count">Fotos del día {activeDay}: {ctxPhotos}/3 {ctxPhotos >= 3 ? '✓' : '(faltan ' + (3 - ctxPhotos) + ')'}</div>
              <div className="ia-reag-btns">
                <button className="ia-btn ia-btn--ghost" onClick={() => setFinishOpen(false)} disabled={busy}>Cancelar</button>
                <button className="ia-btn ia-btn--ok" disabled={busy || !daySummary.trim() || ctxPhotos < 3}
                  onClick={() => doDay('finish-day', { summary: daySummary })}>⏹ Finalizar día {activeDay}</button>
              </div>
            </div>
          ) : (
            <div className="ia-actions-row">
              {canConfirm && <button className="ia-btn ia-btn--ok" disabled={busy} onClick={() => patch({ status: 'confirmada' }, 'Confirmada')}>Confirmar</button>}
              {canReschedule && <button className="ia-btn ia-btn--ghost" disabled={busy} onClick={() => setReagOpen(true)}>Reagendar</button>}
              {canStart && <button className="ia-btn ia-btn--primary" disabled={busy} onClick={() => doDay('start-day')}>▶ Iniciar trabajo{total > 1 ? ` · día ${daysDone + 1}` : ''}</button>}
              {inProgress && isVisit && <button className="ia-btn ia-btn--ok" disabled={busy} onClick={() => doDay('finish-day')}>⏹ Finalizar visita</button>}
              {inProgress && !isVisit && <button className="ia-btn ia-btn--ok" disabled={busy} onClick={() => setFinishOpen(true)}>⏹ Finalizar trabajo{total > 1 ? ` · día ${activeDay}` : ''}</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
